/**
 * Close information that lives on the OPPORTUNITY row (Sales Hub cutover,
 * 2026-09-29, docs/design/sales-hub-cutover-2026-10.md step 4).
 *
 * Before the cutover the close form was the `opportunityclose` activity. From
 * the cutover the rep fills the same fields (and more) on the opportunity
 * before the close, and the close dialog keeps only a date and a note. Every
 * pre-cutover close exists only on the activity, so this is a merge, not a
 * repoint:
 *
 *   - PER RECORD, not per field: when the opportunity carries any close data
 *     it supplies ALL the close-form fields, blanks included. Mixing fields
 *     across the two sources is how a reopened Won-then-Lost deal ends up
 *     showing the first close's products beside the second close's reason.
 *   - The activity still supplies what only it has: the dialog note
 *     (`description`), `actualend` and `actualrevenue`.
 *
 * Pure (no client imports) so the reports-scheduler Function App can use it.
 */

import type { OpportunityClose } from '../api/opportunityApi';

const FV = '@OData.Community.Display.V1.FormattedValue';

/** Opportunity columns that carry close information. Lookups in `_x_value` form. */
export const OPP_CLOSE_SELECT = [
  'opportunityid',
  'dts_closureconfirmation',
  'dts_statusclosereason',
  'dts_statusclosereasonsecondary',
  'dts_firstveracitysaleforbroker',
  '_dts_confirmclientcontacct_value',
  'dts_productssold',
  '_dts_soldtpa_value',
  '_dts_pbmsold_value',
  '_dts_networksold_value',
  '_dts_stoplosscarriersold_value',
  'dts_stoplossplacedby',
  'dts_plantypesold',
  'dts_cobraadministration',
  'dts_consumerdrivenproductssold',
  'dts_telemedicinevendorsold',
  'dts_tpaadminfee',
  'dts_pharmacyfee',
  'dts_medicaladminfeesold',
  'dts_medicalplanssold',
  'dts_feeconcession',
  '_dts_concessionapprovedby_value',
  'dts_openenrollmentstartdate',
  'dts_openenrollmentnotes',
  'dts_outsidevendorsforimplementation',
  'dts_notesforimplementation',
] as const;

/**
 * Server-side prefilter for bulk reads: only opportunities that carry close
 * data. Covers the fields a rep cannot skip on a real close; a row that
 * slips past it is still checked by `oppCloseFields`.
 */
export const OPP_HAS_CLOSE_DATA_FILTER =
  '(dts_statusclosereason ne null or dts_productssold ne null or _dts_soldtpa_value ne null'
  + ' or _dts_pbmsold_value ne null or dts_notesforimplementation ne null)';

export type OppCloseEntity = { opportunityid: string } & Record<string, unknown>;

/** The close-form fields read off the opportunity. */
export type OppCloseFields = Omit<
  OpportunityClose,
  'id' | 'opportunityId' | 'actualRevenue' | 'closeDate' | 'description' | 'source'
>;

function str(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined;
  const s = typeof v === 'string' ? v : String(v);
  return s.trim() === '' ? undefined : s;
}

function fmt(e: Record<string, unknown>, col: string): string | undefined {
  return str(e[`${col}${FV}`]) ?? undefined;
}

function bool(v: unknown): boolean | undefined {
  return typeof v === 'boolean' ? v : undefined;
}

/**
 * Map the opportunity's close columns, or null when it carries no close data
 * (every pre-cutover opportunity), so the caller falls back to the activity.
 */
export function oppCloseFields(e: Record<string, unknown>): OppCloseFields | null {
  const fields: OppCloseFields = {
    closureConfirmed: bool(e.dts_closureconfirmation),
    wonOrLossReason: fmt(e, 'dts_statusclosereason'),
    secondaryCloseReason: fmt(e, 'dts_statusclosereasonsecondary'),
    firstVeracitySaleForBroker: bool(e.dts_firstveracitysaleforbroker),
    confirmClientContact: fmt(e, '_dts_confirmclientcontacct_value'),
    productsSold: fmt(e, 'dts_productssold') ?? str(e.dts_productssold),
    productsSoldRaw: str(e.dts_productssold),
    soldTpa: fmt(e, '_dts_soldtpa_value'),
    pbmSold: fmt(e, '_dts_pbmsold_value'),
    networkSold: fmt(e, '_dts_networksold_value'),
    stopLossCarrierSold: fmt(e, '_dts_stoplosscarriersold_value'),
    stopLossPlacedBy: fmt(e, 'dts_stoplossplacedby'),
    planServicesSold: fmt(e, 'dts_plantypesold'),
    cobraAdministration: fmt(e, 'dts_cobraadministration'),
    consumerDrivenProductsSold: fmt(e, 'dts_consumerdrivenproductssold'),
    telemedicineVendorSold: str(e.dts_telemedicinevendorsold),
    tpaAdminFee: str(e.dts_tpaadminfee),
    pharmacyFee: str(e.dts_pharmacyfee),
    medicalAdminFeeSold: str(e.dts_medicaladminfeesold),
    medicalPlansSold: str(e.dts_medicalplanssold),
    feeConcession: str(e.dts_feeconcession),
    concessionApprovedBy: fmt(e, '_dts_concessionapprovedby_value'),
    openEnrollmentStartDate: str(e.dts_openenrollmentstartdate),
    openEnrollmentNotes: str(e.dts_openenrollmentnotes),
    outsideVendorsForImplementation: str(e.dts_outsidevendorsforimplementation),
    notesForImplementation: str(e.dts_notesforimplementation),
  };
  // Booleans default to false on new rows, so an unticked box is not data.
  const hasData = Object.entries(fields).some(([, v]) =>
    typeof v === 'boolean' ? v : v !== undefined);
  return hasData ? fields : null;
}

/**
 * The close record to show: the opportunity's fields when it has any, laid
 * over the resolved activity for the note and dates. Returns the activity
 * unchanged when the opportunity carries nothing.
 */
export function overlayOppClose(
  opportunityId: string,
  activity: OpportunityClose | null | undefined,
  fields: OppCloseFields | null,
): OpportunityClose | undefined {
  if (!fields) return activity ? { ...activity, source: activity.source ?? 'closeActivity' } : undefined;
  return {
    id: activity?.id ?? `opportunity:${opportunityId}`,
    opportunityId,
    actualRevenue: activity?.actualRevenue,
    closeDate: activity?.closeDate,
    description: activity?.description,
    ...fields,
    source: 'opportunity',
  };
}
